import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Save, X } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { useLabel } from '@/lib/labels';

/** `/settings/employee-categories` (社員区分タブ、v0.2.5 S-11)。既存 /employee-categories API を利用。
 *  パターンは MenuCategoriesPage.tsx に準拠。 */
type Category = { category_id: string; code: string; label: string; sort_order: number; description: string | null };
type EditingRow = { category_id: string; label: string; sort_order: number };

export function EmployeeCategoriesSettingsPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const { data: categories = [], isLoading } = useQuery<Category[]>({ queryKey: ['employee-categories'], queryFn: () => apiFetch<Category[]>('/employee-categories') });

  const [creating, setCreating] = useState<{ code: string; label: string; sort_order: number } | null>(null);
  const [editing, setEditing] = useState<EditingRow | null>(null);
  const [deleting, setDeleting] = useState<Category | null>(null);

  const createMut = useMutation({
    mutationFn: (body: { code: string; label: string; sort_order: number }) => apiFetch('/employee-categories', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employee-categories'] }); setCreating(null); toast.success('社員区分を追加しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '追加に失敗しました'),
  });
  const updateMut = useMutation({
    mutationFn: (r: EditingRow) => apiFetch(`/employee-categories/${r.category_id}`, { method: 'PATCH', body: JSON.stringify({ label: r.label.trim(), sort_order: r.sort_order }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employee-categories'] }); setEditing(null); toast.success('社員区分を更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/employee-categories/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employee-categories'] }); setDeleting(null); toast.success('社員区分を削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleting(null); },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-h2">{t('entity.employee_categories', '社員区分')}</h2>
          <p className="text-sm text-neutral-sub mt-1">正社員・パート等、社員の区分マスタを管理します。</p>
        </div>
        <button type="button" onClick={() => setCreating({ code: '', label: '', sort_order: (categories.reduce((m, c) => Math.max(m, c.sort_order), 0) + 10) })}
          className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover whitespace-nowrap">
          <Plus size={16} /> 区分追加
        </button>
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <th className="text-left px-4 py-2 w-28">{t('field.employee_categories.code', 'コード')}</th>
                <th className="text-left px-4 py-2">{t('field.employee_categories.label', '名称')}</th>
                <th className="text-right px-4 py-2 w-20">{t('field.employee_categories.sort_order', '表示順')}</th>
                <th className="w-24 px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => {
                const isEditing = editing?.category_id === c.category_id;
                return (
                  <tr key={c.category_id} className="border-t border-neutral-line">
                    <td className="px-4 py-2 font-mono text-xs text-neutral-sub">{c.code}</td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                          className="w-full px-2 py-1 rounded border border-neutral-line focus-ring" />
                      ) : c.label}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {isEditing ? (
                        <input type="number" min={0} max={9999} value={editing.sort_order}
                          onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })}
                          className="w-20 px-2 py-1 rounded border border-neutral-line text-right focus-ring" />
                      ) : c.sort_order}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button type="button" aria-label="保存" onClick={() => updateMut.mutate(editing)}
                            disabled={updateMut.isPending || editing.label.trim() === ''}
                            className="p-1 text-brand-red hover:text-brand-red-hover disabled:opacity-40"><Save size={14} /></button>
                          <button type="button" aria-label="キャンセル" onClick={() => setEditing(null)}
                            className="ml-1 p-1 text-neutral-sub hover:text-neutral-text"><X size={14} /></button>
                        </>
                      ) : (
                        <>
                          <button type="button" aria-label="編集"
                            onClick={() => setEditing({ category_id: c.category_id, label: c.label, sort_order: c.sort_order })}
                            className="p-1 text-brand-red hover:text-brand-red-hover"><Pencil size={14} /></button>
                          <button type="button" aria-label="削除" onClick={() => setDeleting(c)}
                            className="ml-1 p-1 text-neutral-sub hover:text-warning-text"><Trash2 size={14} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {categories.length === 0 && <EmptyState message="社員区分が登録されていません" description="「区分追加」から追加してください" />}
        </div>
      )}

      {/* Design MAJOR対応 (2026-08-03レビュー): 自作の fixed/overlay div ではなく共有Modal を使う */}
      <Modal
        open={creating !== null}
        title="社員区分追加"
        onClose={() => setCreating(null)}
        size="sm"
        footer={creating && (
          <>
            <button type="button" onClick={() => setCreating(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
            <button type="submit" form="employee-category-create-form" disabled={createMut.isPending} className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
              {createMut.isPending ? '作成中…' : '作成'}
            </button>
          </>
        )}
      >
        {creating && (
          <form id="employee-category-create-form"
            onSubmit={(e) => {
              e.preventDefault();
              const code = creating.code.trim();
              const label = creating.label.trim();
              if (!code) { toast.error('コードを入力してください'); return; }
              if (!label) { toast.error('名称を入力してください'); return; }
              createMut.mutate({ code, label, sort_order: creating.sort_order });
            }}
            className="space-y-3">
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.employee_categories.code', 'コード')}*</label>
              <input required autoFocus value={creating.code} onChange={(e) => setCreating({ ...creating, code: e.target.value })}
                pattern="[A-Za-z0-9_-]+" className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.employee_categories.label', '名称')}*</label>
              <input required value={creating.label} onChange={(e) => setCreating({ ...creating, label: e.target.value })}
                placeholder="例：正社員、アルバイト" className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="社員区分を削除しますか？"
        destructive
        confirmLabel="削除する"
        loading={deleteMut.isPending}
        onClose={() => { if (!deleteMut.isPending) setDeleting(null); }}
        onConfirm={() => { if (deleting) deleteMut.mutate(deleting.category_id); }}
        message={<p className="text-sm">既に社員に使用されている区分は削除できません (サーバー側でチェックされます)。</p>}
      />
    </div>
  );
}
